import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import type { SceneLayer, SceneDefinition } from '../types/segmentationTypes'
import type {
  ChapterSceneLayer,
  WitnessSceneLayerRecord,
  SceneAssignmentStatus,
  SceneLayerWithStatus,
  CanonicalSceneMetadata,
  SceneBoundaryPhrase,
  CanonicalSceneAnchor
} from '../types/sceneTypes'

export interface MicroUnitReference {
  unitId: string
  chunkId: string
  text: string
  startOffset?: number
  endOffset?: number
}

interface CanonicalSceneDraft {
  scene_id: string
  title: string
  description?: string
  color?: string
  notes?: string
  anchor_unit_id?: string
  anchor_chunk_id?: string
  anchor_witness_id?: string
  anchor_signature?: string
  anchor_confidence?: number
  boundary_phrases?: SceneBoundaryPhrase[]
  ui_metadata?: Record<string, any>
}

interface UpdateCanonicalSceneLayerParams {
  chapterId: string
  bookId: string
  scenes: CanonicalSceneDraft[]
  autoApplyDefault?: boolean
  metadata?: Partial<CanonicalSceneMetadata>
  updatedBy?: string
}

interface SceneStoreState {
  canonicalLayers: Map<string, ChapterSceneLayer>
  witnessLayers: Map<string, WitnessSceneLayerRecord>
  loadingChapters: Set<string>
  loadingWitnesses: Set<string>
  canonicalVersionCounter: Map<string, number>
  microUnitCache: Map<string, MicroUnitReference[]>
  pendingRemapByChapter: Map<string, Set<string>>

  getChapterSceneLayer: (chapterId: string) => ChapterSceneLayer | undefined
  loadChapterSceneLayer: (chapterId: string, opts?: { fallback?: ChapterSceneLayer }) => Promise<ChapterSceneLayer | undefined>
  saveChapterSceneLayer: (layer: ChapterSceneLayer) => Promise<void>
  updateCanonicalSceneLayer: (params: UpdateCanonicalSceneLayerParams) => Promise<ChapterSceneLayer>

  getWitnessSceneLayer: (witnessId: string) => WitnessSceneLayerRecord | undefined
  loadWitnessSceneLayer: (
    witnessId: string,
    chapterId: string,
    bookId: string,
    opts?: { fallback?: SceneLayer; canonicalVersionHint?: number; allowLegacyFallback?: boolean }
  ) => Promise<WitnessSceneLayerRecord | undefined>
  saveWitnessSceneLayer: (
    record: WitnessSceneLayerRecord,
    opts?: { expectedUpdatedAt?: string; allowCreate?: boolean }
  ) => Promise<void>

  buildLayerStatus: (
    witnessLayer: WitnessSceneLayerRecord | undefined,
    canonicalLayer: ChapterSceneLayer | undefined
  ) => SceneLayerWithStatus | undefined

  createWitnessRecordFromSceneLayer: (
    witnessId: string,
    chapterId: string,
    bookId: string,
    layer: SceneLayer,
    canonicalVersion: number,
    assignmentStatus?: SceneAssignmentStatus
  ) => WitnessSceneLayerRecord

  ensureAutoMappedWitnessLayer: (params: {
    witnessId: string
    chapterId: string
    bookId: string
    microUnits: MicroUnitReference[]
  }, opts?: { force?: boolean }) => Promise<WitnessSceneLayerRecord | undefined>

  confirmAutoAssignments: (witnessId: string) => Promise<void>

  registerMicroUnits: (witnessId: string, microUnits: MicroUnitReference[]) => void
  unregisterMicroUnits: (witnessId: string) => void

  promoteWitnessScenesToCanonical: (params: {
    witnessId: string
    chapterId: string
    bookId: string
    microUnits: MicroUnitReference[]
    promotedBy?: string
  }) => Promise<ChapterSceneLayer>

  applyCanonicalScenesToWitness: (params: {
    witnessId: string
    chapterId: string
    bookId: string
    microUnits: MicroUnitReference[]
  }) => Promise<WitnessSceneLayerRecord | undefined>
}

function nowIso(): string {
  return new Date().toISOString()
}

const DEFAULT_STATUS: SceneAssignmentStatus = 'confirmed'

function normalizeText(value: string | undefined): string {
  return (value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function generateSceneId(baseTitle: string, used: Set<string>): string {
  const base = normalizeText(baseTitle)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
  const fallback = base.length > 0 ? base : 'scene'
  let candidate = fallback
  let counter = 2
  while (used.has(candidate)) {
    candidate = `${fallback}-${counter}`
    counter += 1
  }
  used.add(candidate)
  return candidate
}

const WORD_REGEX = /[\p{L}\p{N}’']+/gu
const STOPWORDS = new Set([
  'the', 'and', 'or', 'to', 'of', 'in', 'a', 'an', 'for', 'on', 'with', 'as', 'by',
  'that', 'this', 'these', 'those', 'be', 'is', 'are', 'was', 'were', 'it', 'its',
  'at', 'from', 'but', 'if', 'then', 'so', 'yet', 'not', 'no', 'nor', 'do', 'does',
  'did', 'have', 'has', 'had', 'into', 'over', 'after', 'before', 'about', 'between',
  'through', 'during', 'without', 'within', 'upon', 'their', 'them', 'his', 'her',
  'him', 'she', 'he', 'we', 'you', 'they', 'our', 'your', 'my', 'mine', 'yours',
  'ours', 'theirs', 'someone', 'something', 'anyone', 'anything', 'each', 'all',
  'more', 'most', 'some', 'few', 'many', 'such', 'which', 'who', 'whom', 'whose',
  'what', 'when', 'where', 'why', 'how', 'there', 'here', 'out', 'up', 'down', 'left',
  'right', 'again', 'further', 'once', 'ever', 'never', 'also', 'very', 'can', 'will',
  'shall', 'may', 'might', 'should', 'would', 'could'
])

function tokenize(text: string): string[] {
  return (text.match(WORD_REGEX) ?? []).map(token => token.toLowerCase())
}

function hasInformativeContent(tokens: string[]): boolean {
  const meaningful = tokens.filter(token => token.length > 2 && !STOPWORDS.has(token))
  return meaningful.length >= Math.ceil(tokens.length / 2)
}

function derivePhrasesFromSignature(signature: string, max = 3): string[] {
  const tokens = tokenize(signature)
  if (tokens.length === 0) {
    return []
  }

  const candidates: string[] = []
  const minLength = Math.min(4, Math.max(2, tokens.length))
  const maxLength = Math.min(6, tokens.length)

  for (let length = maxLength; length >= minLength; length -= 1) {
    for (let start = 0; start <= tokens.length - length; start += 1) {
      const slice = tokens.slice(start, start + length)
      if (!hasInformativeContent(slice)) {
        continue
      }
      const phrase = slice.join(' ')
      if (!candidates.includes(phrase)) {
        candidates.push(phrase)
        if (candidates.length >= max) {
          return candidates
        }
      }
    }
  }

  if (candidates.length === 0) {
    return [tokens.join(' ')]
  }

  return candidates
}

function collectSceneBoundaryPhrases(canonical: ChapterSceneLayer): Map<string, SceneBoundaryPhrase[]> {
  const result = new Map<string, SceneBoundaryPhrase[]>()

  canonical.scene_map.forEach(scene => {
    const existing = Array.isArray(scene.boundary_phrases) ? scene.boundary_phrases : []
    if (existing.length > 0) {
      result.set(scene.scene_id, existing.map(entry => ({
        phrase: entry.phrase,
        confidence: entry.confidence ?? 0.8,
        source: entry.source ?? 'editor'
      })))
    }
  })

  canonical.canonical_anchors.forEach(anchor => {
    const sceneId = anchor.scene_id
    if (!sceneId) {
      return
    }

    if (!result.has(sceneId)) {
      result.set(sceneId, [])
    }

    if (anchor.reference_text_signature) {
      const baseConfidence = anchor.confidence ?? 0.9
      const list = result.get(sceneId)!
      list.push({
        phrase: anchor.reference_text_signature,
        confidence: baseConfidence,
        source: 'canonical_start_unit'
      })

      derivePhrasesFromSignature(anchor.reference_text_signature).forEach(derived => {
        list.push({
          phrase: derived,
          confidence: Math.min(baseConfidence - 0.1, 0.75),
          source: 'derived_ngram'
        })
      })
    }
  })

  canonical.scene_map.forEach(scene => {
    if (!result.has(scene.scene_id)) {
      result.set(scene.scene_id, [])
    }
  })

  const deduped = new Map<string, SceneBoundaryPhrase[]>()
  result.forEach((phrases, sceneId) => {
    const seen = new Set<string>()
    const filtered: SceneBoundaryPhrase[] = []
    phrases.forEach(entry => {
      const normalized = normalizeText(entry.phrase)
      if (!normalized || seen.has(normalized)) {
        return
      }
      seen.add(normalized)
      filtered.push(entry)
    })
    deduped.set(sceneId, filtered)
  })

  return deduped
}

function registerSceneStartCandidate(
  startMap: Map<string, { index: number; confidence: number }>,
  sceneId: string,
  index: number,
  confidence: number
) {
  if (index < 0) {
    return
  }
  const existing = startMap.get(sceneId)
  if (!existing || confidence > existing.confidence || (confidence === existing.confidence && index < existing.index)) {
    startMap.set(sceneId, { index, confidence })
  }
}

function computeTokenFrequencies(unitTokens: string[][]): Map<string, number> {
  const frequencies = new Map<string, number>()
  unitTokens.forEach(tokens => {
    tokens.forEach(token => {
      if (!token) {
        return
      }
      frequencies.set(token, (frequencies.get(token) ?? 0) + 1)
    })
  })
  return frequencies
}

function scorePhraseAgainstUnit(
  phraseTokens: string[],
  unitTokens: string[],
  tokenFrequencies: Map<string, number>
): { coverage: number; rarity: number } {
  if (phraseTokens.length === 0 || unitTokens.length === 0) {
    return { coverage: 0, rarity: 0 }
  }

  const unitTokenCounts = new Map<string, number>()
  unitTokens.forEach(token => {
    unitTokenCounts.set(token, (unitTokenCounts.get(token) ?? 0) + 1)
  })

  let matches = 0
  let rarityAccumulator = 0
  phraseTokens.forEach(token => {
    if (!token) {
      return
    }
    if (unitTokenCounts.has(token)) {
      matches += 1
      const freq = tokenFrequencies.get(token) ?? unitTokens.length
      rarityAccumulator += 1 / Math.max(freq, 1)
    }
  })

  if (matches === 0) {
    return { coverage: 0, rarity: 0 }
  }

  const coverage = matches / phraseTokens.length
  const rarity = rarityAccumulator / matches
  return { coverage, rarity }
}

function findBestMatchForPhrase(
  phrase: string,
  normalizedUnitTexts: string[],
  unitTokens: string[][],
  tokenFrequencies: Map<string, number>
): { index: number; confidence: number } | undefined {
  const normalizedPhrase = normalizeText(phrase)
  if (!normalizedPhrase) {
    return undefined
  }

  const directIndex = normalizedUnitTexts.findIndex(text => text.includes(normalizedPhrase))
  if (directIndex !== -1) {
    return { index: directIndex, confidence: 0.88 }
  }

  const phraseTokens = tokenize(phrase)
  if (phraseTokens.length === 0) {
    return undefined
  }

  let bestIndex = -1
  let bestScore = 0
  let bestCoverage = 0

  unitTokens.forEach((tokens, idx) => {
    if (!tokens || tokens.length === 0) {
      return
    }
    const { coverage, rarity } = scorePhraseAgainstUnit(phraseTokens, tokens, tokenFrequencies)
    if (coverage === 0) {
      return
    }
    const score = (coverage * 0.8) + (rarity * 0.2)
    if (score > bestScore || (score === bestScore && coverage > bestCoverage)) {
      bestScore = score
      bestCoverage = coverage
      bestIndex = idx
    }
  })

  if (bestIndex === -1 || bestCoverage < 0.5) {
    return undefined
  }

  const confidence = Math.min(0.5 + bestScore * 0.45, 0.92)
  return { index: bestIndex, confidence }
}

function shouldAutoRemapWitness(
  record: WitnessSceneLayerRecord | undefined,
  canonicalLayer: ChapterSceneLayer
): boolean {
  if (!record || !canonicalLayer) {
    return false
  }

  if (canonicalLayer.metadata?.auto_apply_default === false) {
    return false
  }

  const canonicalVersion = canonicalLayer.version ?? 0
  const witnessVersion = record.canonical_chapter_version ?? 0
  if (witnessVersion >= canonicalVersion) {
    return false
  }
  const metadata = (record.metadata ?? {}) as Record<string, unknown>
  if ((metadata as any).auto_applied === false) {
    return false
  }
  const strategyValue = (metadata as any).strategy
  const strategy = typeof strategyValue === 'string' ? strategyValue.toLowerCase() : undefined
  if (strategy && strategy !== 'auto_map_from_canonical') {
    return false
  }
  return true
}

export const useSceneStore = create<SceneStoreState>((set, get) => {
  const cacheMicroUnits = (witnessId: string, microUnits: MicroUnitReference[]) => {
    const existing = get().microUnitCache.get(witnessId)
    if (!microUnits || microUnits.length === 0) {
      if (!existing) {
        return
      }
    } else if (existing === microUnits) {
      return
    }
    set(prev => {
      const nextCache = new Map(prev.microUnitCache)
      if (!microUnits || microUnits.length === 0) {
        nextCache.delete(witnessId)
      } else {
        nextCache.set(witnessId, microUnits)
      }
      return { microUnitCache: nextCache }
    })
  }

  const removePendingRemap = (witnessId: string, chapterId: string) => {
    const pending = get().pendingRemapByChapter.get(chapterId)
    if (!pending || !pending.has(witnessId)) {
      return
    }
    set(prev => {
      const nextPending = new Map(prev.pendingRemapByChapter)
      const updatedSet = new Set(nextPending.get(chapterId) ?? [])
      updatedSet.delete(witnessId)
      if (updatedSet.size === 0) {
        nextPending.delete(chapterId)
      } else {
        nextPending.set(chapterId, updatedSet)
      }
      return { pendingRemapByChapter: nextPending }
    })
  }

  const scheduleBackgroundRemap = (chapterId: string) => {
    setTimeout(() => {
      const state = get()
      const pending = state.pendingRemapByChapter.get(chapterId)
      if (!pending || pending.size === 0) {
        return
      }
      pending.forEach(async (witnessId) => {
        const record = state.witnessLayers.get(witnessId)
        const microUnits = state.microUnitCache.get(witnessId)
        if (!record || !microUnits || microUnits.length === 0) {
          return
        }
        try {
          await state.ensureAutoMappedWitnessLayer(
            {
              witnessId,
              chapterId: record.chapter_id,
              bookId: record.book_id,
              microUnits
            },
            { force: true }
          )
        } catch (error) {
          console.error('SceneStore background remap failed', { witnessId, chapterId }, error)
        }
      })
    }, 0)
  }

  return {
    canonicalLayers: new Map(),
    witnessLayers: new Map(),
    loadingChapters: new Set(),
    loadingWitnesses: new Set(),
    canonicalVersionCounter: new Map(),
    microUnitCache: new Map(),
    pendingRemapByChapter: new Map(),

  getChapterSceneLayer: (chapterId) => {
    return get().canonicalLayers.get(chapterId)
  },

  loadChapterSceneLayer: async (chapterId, opts) => {
    const state = get()
    if (state.canonicalLayers.has(chapterId)) {
      return state.canonicalLayers.get(chapterId)
    }

    if (state.loadingChapters.has(chapterId)) {
      // Wait until current load completes
      await new Promise(resolve => setTimeout(resolve, 50))
      return get().canonicalLayers.get(chapterId)
    }

    state.loadingChapters.add(chapterId)
    try {
      const existing = await dbStorage.get('chapter_scenes', chapterId)
      if (existing) {
        const metadata: CanonicalSceneMetadata = existing.metadata && 'created_at' in existing.metadata && 'updated_at' in existing.metadata
          ? existing.metadata as CanonicalSceneMetadata
          : {
              created_at: existing.created_at,
              updated_at: existing.updated_at,
              ...existing.metadata
            }
        const sceneMap = (existing.scene_map || []).map(scene => ({
          ...scene,
          created_at: scene.created_at ?? existing.created_at
        }))
        const normalized: ChapterSceneLayer = {
          ...existing,
          metadata,
          scene_map: sceneMap
        }
        set(prev => {
          const next = new Map(prev.canonicalLayers)
          next.set(chapterId, normalized)
          const versionTracker = new Map(prev.canonicalVersionCounter)
          versionTracker.set(chapterId, normalized.version)
          return { canonicalLayers: next, canonicalVersionCounter: versionTracker }
        })
        return normalized
      }

      if (opts?.fallback) {
        const fallbackMetadata: CanonicalSceneMetadata = opts.fallback.metadata ?? {
          created_at: opts.fallback.created_at,
          updated_at: opts.fallback.updated_at
        }
        const fallbackSceneCreatedAt = opts.fallback.created_at
        const fallbackSceneMap = (opts.fallback.scene_map || []).map(scene => ({
          ...scene,
          created_at: scene.created_at ?? fallbackSceneCreatedAt
        }))
        const fallbackLayer: ChapterSceneLayer = {
          ...opts.fallback,
          metadata: fallbackMetadata,
          scene_map: fallbackSceneMap
        }
        await dbStorage.put('chapter_scenes', fallbackLayer)
        set(prev => {
          const next = new Map(prev.canonicalLayers)
          next.set(chapterId, fallbackLayer)
          const versionTracker = new Map(prev.canonicalVersionCounter)
          versionTracker.set(chapterId, fallbackLayer.version)
          return { canonicalLayers: next, canonicalVersionCounter: versionTracker }
        })
        return fallbackLayer
      }
    } finally {
      set(prev => {
        const next = new Set(prev.loadingChapters)
        next.delete(chapterId)
        return { loadingChapters: next }
      })
    }
    return undefined
  },

  saveChapterSceneLayer: async (layer) => {
    await dbStorage.put('chapter_scenes', layer)
    const witnessesNeedingRemap: string[] = []
    set(prev => {
      const next = new Map(prev.canonicalLayers)
      next.set(layer.chapter_id, layer)
      const tracker = new Map(prev.canonicalVersionCounter)
      tracker.set(layer.chapter_id, layer.version)
      const nextPending = new Map(prev.pendingRemapByChapter)

      prev.witnessLayers.forEach((record, witnessId) => {
        if (record.chapter_id !== layer.chapter_id) {
          return
        }
        if (!shouldAutoRemapWitness(record, layer)) {
          return
        }
        const pendingSet = new Set(nextPending.get(layer.chapter_id) ?? [])
        pendingSet.add(witnessId)
        nextPending.set(layer.chapter_id, pendingSet)
        witnessesNeedingRemap.push(witnessId)
      })

      return {
        canonicalLayers: next,
        canonicalVersionCounter: tracker,
        pendingRemapByChapter: nextPending
      }
    })
    if (witnessesNeedingRemap.length > 0) {
      scheduleBackgroundRemap(layer.chapter_id)
    }
  },

  updateCanonicalSceneLayer: async ({ chapterId, bookId, scenes, autoApplyDefault, metadata, updatedBy }) => {
    if (!Array.isArray(scenes) || scenes.length === 0) {
      throw new Error('Canonical scene editor requires at least one scene')
    }

    const now = nowIso()
    const existing = get().canonicalLayers.get(chapterId)
    const existingSceneMap = new Map(
      (existing?.scene_map ?? []).map(scene => [scene.scene_id, scene])
    )
    const existingAnchors = new Map(
      (existing?.canonical_anchors ?? []).map(anchor => [anchor.scene_id, anchor])
    )

    const usedIds = new Set<string>()
    const sanitizedScenes = scenes.map((scene, index) => {
      const trimmedId = scene.scene_id?.trim()
      let sceneId: string
      if (trimmedId && trimmedId.length > 0) {
        sceneId = trimmedId
        if (usedIds.has(sceneId)) {
          sceneId = generateSceneId(sceneId, usedIds)
        } else {
          usedIds.add(sceneId)
        }
      } else {
        sceneId = generateSceneId(scene.title || `scene-${index + 1}`, usedIds)
      }
      return {
        ...scene,
        scene_id: sceneId
      }
    })

    const sceneOrder = sanitizedScenes.map(scene => scene.scene_id)

    const canonicalAnchors: CanonicalSceneAnchor[] = []
    sanitizedScenes.forEach(scene => {
      const previous = existingAnchors.get(scene.scene_id)
      const witnessId = scene.anchor_witness_id ?? previous?.reference_witness_id
      const unitId = scene.anchor_unit_id ?? previous?.reference_unit_id
      const chunkId = scene.anchor_chunk_id ?? previous?.reference_chunk_id
      const signature = (scene.anchor_signature ?? previous?.reference_text_signature)?.trim()
      const confidenceValue =
        typeof scene.anchor_confidence === 'number'
          ? Math.max(0, Math.min(1, scene.anchor_confidence))
          : previous?.confidence ?? (signature ? 0.92 : undefined)

      if (!witnessId && !signature && !unitId) {
        return
      }

      canonicalAnchors.push({
        scene_id: scene.scene_id,
        reference_witness_id: witnessId ?? previous?.reference_witness_id ?? '',
        reference_unit_id: unitId,
        reference_chunk_id: chunkId,
        reference_text_signature: signature,
        confidence: confidenceValue
      })
    })

    const anchorLookup = new Map(canonicalAnchors.map(anchor => [anchor.scene_id, anchor]))

    const sceneMap = sanitizedScenes.map(scene => {
      const previous = existingSceneMap.get(scene.scene_id)
      const anchor = anchorLookup.get(scene.scene_id) ?? existingAnchors.get(scene.scene_id)
      const manualPhrases = (scene.boundary_phrases ?? previous?.boundary_phrases ?? []).filter(
        phrase => phrase?.source !== 'canonical_start_unit' && phrase?.source !== 'derived_ngram'
      )

      const boundaryPhrases: SceneBoundaryPhrase[] = []
      const seen = new Set<string>()
      const addPhrase = (entry: SceneBoundaryPhrase) => {
        if (!entry?.phrase) {
          return
        }
        const key = normalizeText(entry.phrase)
        if (seen.has(key)) {
          return
        }
        seen.add(key)
        boundaryPhrases.push({
          phrase: entry.phrase,
          confidence: entry.confidence,
          source: entry.source ?? 'editor'
        })
      }

      manualPhrases.forEach(addPhrase)

      const signature = anchor?.reference_text_signature
      if (signature) {
        addPhrase({
          phrase: signature,
          confidence: anchor?.confidence ?? 0.95,
          source: 'canonical_start_unit'
        })
        const derivedConfidenceBase =
          typeof anchor?.confidence === 'number' ? Math.max(anchor.confidence - 0.1, 0.5) : 0.75
        const derivedConfidence = Math.min(Math.max(derivedConfidenceBase, 0.4), 0.85)
        derivePhrasesFromSignature(signature).forEach(derived => {
          addPhrase({
            phrase: derived,
            confidence: derivedConfidence,
            source: 'derived_ngram'
          })
        })
      } else if (boundaryPhrases.length === 0 && previous?.boundary_phrases) {
        previous.boundary_phrases.forEach(addPhrase)
      }

      return {
        scene_id: scene.scene_id,
        title: scene.title || previous?.title || scene.scene_id,
        description: scene.description ?? previous?.description,
        color: scene.color ?? previous?.color,
        anchor_unit_id: scene.anchor_unit_id ?? previous?.anchor_unit_id,
        anchor_chunk_id: scene.anchor_chunk_id ?? previous?.anchor_chunk_id,
        created_at: previous?.created_at ?? now,
        notes: scene.notes ?? previous?.notes,
        ui_metadata: {
          ...(previous?.ui_metadata ?? {}),
          ...(scene.ui_metadata ?? {})
        },
        boundary_phrases: boundaryPhrases
      }
    })

    const baseMetadata: CanonicalSceneMetadata = {
      created_at: existing?.metadata?.created_at ?? existing?.created_at ?? now,
      updated_at: now,
      created_by: existing?.metadata?.created_by,
      updated_by: existing?.metadata?.updated_by,
      source_witness_id: existing?.metadata?.source_witness_id,
      notes: existing?.metadata?.notes,
      auto_apply_default: existing?.metadata?.auto_apply_default ?? true
    }

    const mergedMetadata: CanonicalSceneMetadata = {
      ...baseMetadata,
      ...metadata,
      updated_at: now,
      updated_by: metadata?.updated_by ?? updatedBy ?? baseMetadata.updated_by ?? 'matrix-editor'
    }

    if (autoApplyDefault !== undefined) {
      mergedMetadata.auto_apply_default = autoApplyDefault
    } else if (mergedMetadata.auto_apply_default === undefined) {
      mergedMetadata.auto_apply_default = true
    }

    const canonicalLayer: ChapterSceneLayer = {
      chapter_id: chapterId,
      book_id: bookId,
      version: (existing?.version ?? 0) + 1,
      scene_order: sceneOrder,
      scene_map: sceneMap,
      canonical_anchors: canonicalAnchors,
      metadata: mergedMetadata,
      created_at: existing?.created_at ?? now,
      updated_at: now
    }

    await get().saveChapterSceneLayer(canonicalLayer)
    return canonicalLayer
  },

  getWitnessSceneLayer: (witnessId) => {
    return get().witnessLayers.get(witnessId)
  },

  loadWitnessSceneLayer: async (witnessId, chapterId, bookId, opts) => {
    const state = get()
    if (state.witnessLayers.has(witnessId)) {
      return state.witnessLayers.get(witnessId)
    }

    if (state.loadingWitnesses.has(witnessId)) {
      await new Promise(resolve => setTimeout(resolve, 50))
      return get().witnessLayers.get(witnessId)
    }

    state.loadingWitnesses.add(witnessId)
    try {
      const existing = await dbStorage.get('witness_scenes', witnessId)
      if (existing) {
        set(prev => {
          const next = new Map(prev.witnessLayers)
          next.set(witnessId, existing)
          return { witnessLayers: next }
        })
        return existing
      }

      if (opts?.allowLegacyFallback && opts?.fallback) {
        const canonicalVersion =
          opts.canonicalVersionHint ??
          get().canonicalVersionCounter.get(chapterId) ??
          1
        const record = get().createWitnessRecordFromSceneLayer(
          witnessId,
          chapterId,
          bookId,
          opts.fallback,
          canonicalVersion,
          'confirmed'
        )
        await dbStorage.put('witness_scenes', record)
        set(prev => {
          const next = new Map(prev.witnessLayers)
          next.set(witnessId, record)
          return { witnessLayers: next }
        })
        return record
      }
    } finally {
      set(prev => {
        const next = new Set(prev.loadingWitnesses)
        next.delete(witnessId)
        return { loadingWitnesses: next }
      })
    }

    return undefined
  },

  saveWitnessSceneLayer: async (record, opts = {}) => {
    const existing = await dbStorage.get('witness_scenes', record.witness_id)
    const now = nowIso()

    if (existing) {
      const expected = opts.expectedUpdatedAt
      if (expected && existing.updated_at && existing.updated_at !== expected) {
        const conflict = new Error('Scene layer has been updated elsewhere')
        ;(conflict as any).code = 'SCENE_LAYER_CONFLICT'
        throw conflict
      }

      const mergedRecord: WitnessSceneLayerRecord = {
        ...existing,
        ...record,
        created_at: existing.created_at,
        updated_at: now
      }

      await dbStorage.put('witness_scenes', mergedRecord)
      set(prev => {
        const next = new Map(prev.witnessLayers)
        next.set(record.witness_id, mergedRecord)
        const nextPending = new Map(prev.pendingRemapByChapter)
        const pendingSet = nextPending.get(record.chapter_id)
        if (pendingSet?.has(record.witness_id)) {
          const updated = new Set(pendingSet)
          updated.delete(record.witness_id)
          if (updated.size === 0) {
            nextPending.delete(record.chapter_id)
          } else {
            nextPending.set(record.chapter_id, updated)
          }
        }
        return { witnessLayers: next, pendingRemapByChapter: nextPending }
      })
      return
    }

    if (opts.expectedUpdatedAt && !opts.allowCreate) {
      const conflict = new Error('Scene layer record missing; cannot save with stale reference')
      ;(conflict as any).code = 'SCENE_LAYER_CONFLICT'
      throw conflict
    }

    const createdAt = record.created_at ?? now
    const initialRecord: WitnessSceneLayerRecord = {
      ...record,
      created_at: createdAt,
      updated_at: now
    }

    await dbStorage.put('witness_scenes', initialRecord)
    set(prev => {
      const next = new Map(prev.witnessLayers)
      next.set(record.witness_id, initialRecord)
      const nextPending = new Map(prev.pendingRemapByChapter)
      const pendingSet = nextPending.get(record.chapter_id)
      if (pendingSet?.has(record.witness_id)) {
        const updated = new Set(pendingSet)
        updated.delete(record.witness_id)
        if (updated.size === 0) {
          nextPending.delete(record.chapter_id)
        } else {
          nextPending.set(record.chapter_id, updated)
        }
      }
      return { witnessLayers: next, pendingRemapByChapter: nextPending }
    })
  },

  buildLayerStatus: (witnessLayer, canonicalLayer) => {
    if (!witnessLayer && !canonicalLayer) {
      return undefined
    }

    const canonicalVersion = canonicalLayer?.version ?? witnessLayer?.canonical_chapter_version ?? 1

    const assignmentSceneIds = witnessLayer?.assignments?.map(assignment => assignment.scene_id) ?? []
    let sceneOrder: string[] = []
    if (canonicalLayer?.scene_order && canonicalLayer.scene_order.length > 0) {
      sceneOrder = [...canonicalLayer.scene_order]
    } else if (canonicalLayer?.scene_map && canonicalLayer.scene_map.length > 0) {
      sceneOrder = canonicalLayer.scene_map.map(scene => scene.scene_id)
    } else {
      sceneOrder = [...assignmentSceneIds]
    }

    assignmentSceneIds.forEach(sceneId => {
      if (!sceneOrder.includes(sceneId)) {
        sceneOrder.push(sceneId)
      }
    })

    if (witnessLayer?.overrides) {
      witnessLayer.overrides.forEach(override => {
        if (!sceneOrder.includes(override.scene_id)) {
          sceneOrder.push(override.scene_id)
        }
      })
    }

    const overrideMap = new Map(
      (witnessLayer?.overrides ?? []).map(override => [override.scene_id, override])
    )
    const anchorMap = new Map(
      (canonicalLayer?.canonical_anchors ?? []).map(anchor => [anchor.scene_id, anchor])
    )
    const assignmentMap = new Map(
      (witnessLayer?.assignments ?? []).map(assignment => [assignment.scene_id, assignment])
    )

    const canonicalCreatedFallback =
      canonicalLayer?.metadata?.created_at ?? canonicalLayer?.created_at ?? nowIso()
    const witnessCreatedFallback = witnessLayer?.created_at ?? nowIso()

    const sceneDefinitions = new Map<string, SceneDefinition>()

    ;(canonicalLayer?.scene_map ?? []).forEach(scene => {
      const override = overrideMap.get(scene.scene_id)
      const anchor = anchorMap.get(scene.scene_id)
      const overrideNotes = override?.metadata ? JSON.stringify(override.metadata) : undefined
      const canonicalNotes = scene.ui_metadata ? JSON.stringify(scene.ui_metadata) : undefined
      sceneDefinitions.set(scene.scene_id, {
        scene_id: scene.scene_id,
        title: override?.title ?? scene.title ?? scene.scene_id,
        description:
          (override?.metadata?.description as string | undefined) ??
          scene.description,
        color: override?.color ?? scene.color,
        anchor_unit_id: anchor?.reference_unit_id ?? scene.anchor_unit_id,
        anchor_chunk_id: anchor?.reference_chunk_id ?? scene.anchor_chunk_id,
        anchor_offset: scene.anchor_offset,
        created_at: scene.created_at ?? canonicalCreatedFallback,
        notes: overrideNotes ?? canonicalNotes
      })
    })

    overrideMap.forEach((override, sceneId) => {
      if (sceneDefinitions.has(sceneId)) {
        return
      }
      const assignment = assignmentMap.get(sceneId)
      sceneDefinitions.set(sceneId, {
        scene_id: sceneId,
        title: override.title ?? sceneId,
        description: override.metadata?.description,
        color: override.color,
        anchor_unit_id: assignment?.start_unit_id,
        anchor_chunk_id: assignment?.start_chunk_id,
        created_at: witnessCreatedFallback,
        notes: override.metadata ? JSON.stringify(override.metadata) : undefined
      })
    })

    assignmentMap.forEach((assignment, sceneId) => {
      if (sceneDefinitions.has(sceneId)) {
        return
      }
      sceneDefinitions.set(sceneId, {
        scene_id: sceneId,
        title: sceneId,
        color: undefined,
        anchor_unit_id: assignment.start_unit_id,
        anchor_chunk_id: assignment.start_chunk_id,
        anchor_offset: undefined,
        created_at: witnessCreatedFallback,
        notes: undefined
      })
    })

    const sceneOrderSource =
      canonicalLayer?.scene_order && canonicalLayer.scene_order.length > 0
        ? 'canonical_order'
        : canonicalLayer?.scene_map && canonicalLayer.scene_map.length > 0
          ? 'canonical_map'
          : 'witness_assignments'

    let sceneMap = sceneOrder
      .map(sceneId => sceneDefinitions.get(sceneId))
      .filter((entry): entry is SceneDefinition => Boolean(entry))

    sceneDefinitions.forEach((entry, sceneId) => {
      if (!sceneOrder.includes(sceneId)) {
        sceneMap.push(entry)
      }
    })

    const overriddenSceneIds = (witnessLayer?.overrides ?? []).map(override => override.scene_id)

    const assignments = (witnessLayer?.assignments || []).map(assignment => ({
      ...assignment,
      origin: assignment.status,
      start_offset: undefined,
      end_offset: undefined
    }))

    const hasAutoAssignments = assignments.some(assignment => assignment.origin === 'auto')
    const status: SceneLayerWithStatus['status'] =
      (witnessLayer && canonicalLayer && witnessLayer.canonical_chapter_version < canonicalLayer.version) || hasAutoAssignments
        ? 'needs_review'
        : 'in_sync'

    return {
      layer: {
        version: canonicalVersion,
        scene_map: sceneMap ?? [],
        scene_order: sceneOrder ?? [],
        assignments,
        metadata: {
          ...(witnessLayer?.metadata || {}),
          canonical_version: canonicalLayer?.version,
          witness_version: witnessLayer?.canonical_chapter_version,
          scene_order_source: sceneOrderSource,
          override_count: overriddenSceneIds.length,
          overridden_scene_ids: overriddenSceneIds,
          auto_assignment_count: assignments.filter(assignment => assignment.origin === 'auto').length,
          has_auto_assignments: hasAutoAssignments
        }
      },
      status,
      metadata: {
        canonical_version: canonicalLayer?.version ?? canonicalVersion,
        witness_version: witnessLayer?.canonical_chapter_version,
        last_reviewed_at: witnessLayer?.updated_at,
        pending_changes: status === 'needs_review',
        inherited_from_canonical: canonicalLayer ? witnessLayer?.canonical_chapter_version === canonicalLayer.version : false
      }
    }
  },

  createWitnessRecordFromSceneLayer: (witnessId, chapterId, bookId, layer, canonicalVersion, assignmentStatus = DEFAULT_STATUS) => {
    const timestamp = nowIso()
    return {
      witness_id: witnessId,
      chapter_id: chapterId,
      book_id: bookId,
      canonical_chapter_version: canonicalVersion,
      assignments: (layer.assignments || []).map(assignment => ({
        scene_id: assignment.scene_id,
        start_unit_id: assignment.start_unit_id,
        end_unit_id: assignment.end_unit_id,
        start_chunk_id: assignment.start_chunk_id,
        end_chunk_id: assignment.end_chunk_id,
        confidence: assignment.confidence,
        status: assignmentStatus,
        last_reviewed_at: assignmentStatus === 'auto' ? undefined : timestamp
      })),
      overrides: (layer.scene_map || []).map(scene => ({
        scene_id: scene.scene_id,
        title: scene.title,
        color: scene.color,
        metadata: scene.description ? { description: scene.description } : undefined
      })),
      extras: undefined,
      metadata: layer.metadata,
      created_at: timestamp,
      updated_at: timestamp
    }
  },

  ensureAutoMappedWitnessLayer: async ({ witnessId, chapterId, bookId, microUnits }, options = {}) => {
    if (!microUnits || microUnits.length === 0) {
      return undefined
    }

    cacheMicroUnits(witnessId, microUnits)

    let existingRecord: WitnessSceneLayerRecord | undefined = options.force
      ? undefined
      : get().witnessLayers.get(witnessId)

    if (!existingRecord && !options.force) {
      const persistedRecord = await dbStorage.get('witness_scenes', witnessId)
      if (persistedRecord) {
        set(prev => {
          const next = new Map(prev.witnessLayers)
          next.set(witnessId, persistedRecord)
          return { witnessLayers: next }
        })
        existingRecord = persistedRecord
      }
    }

    let canonicalLayer = get().canonicalLayers.get(chapterId)
    if (!canonicalLayer) {
      await get().loadChapterSceneLayer(chapterId)
      canonicalLayer = get().canonicalLayers.get(chapterId)
    }

    if (!canonicalLayer) {
      removePendingRemap(witnessId, chapterId)
      return existingRecord
    }

    if (!options.force && canonicalLayer.metadata?.auto_apply_default === false) {
      removePendingRemap(witnessId, chapterId)
      return existingRecord
    }

    if (existingRecord && !options.force) {
      const canonicalVersion = canonicalLayer.version ?? 0
      const witnessVersion = existingRecord.canonical_chapter_version ?? 0
      if (canonicalVersion <= witnessVersion) {
        removePendingRemap(witnessId, chapterId)
        return existingRecord
      }
    }

    const canonical = canonicalLayer

    const totalUnits = microUnits.length
    if (totalUnits === 0) {
      removePendingRemap(witnessId, chapterId)
      return undefined
    }

    const unitIndexById = new Map<string, number>()
    const normalizedTextIndex = new Map<string, number>()
    const normalizedUnitTexts: string[] = []
    const unitTokensList: string[][] = []

    microUnits.forEach((entry, index) => {
      unitIndexById.set(entry.unitId, index)
      const normalized = normalizeText(entry.text)
      if (normalized && !normalizedTextIndex.has(normalized)) {
        normalizedTextIndex.set(normalized, index)
      }
      normalizedUnitTexts.push(normalized)
      unitTokensList.push(tokenize(entry.text))
    })

    const tokenFrequencies = computeTokenFrequencies(unitTokensList)

    const boundaryIndexByScene = new Map<string, { index: number; confidence: number }>()
    const registerBoundary = (sceneId: string, index: number, confidence: number) => {
      if (index < 0 || index >= totalUnits) {
        return
      }
      const existing = boundaryIndexByScene.get(sceneId)
      if (!existing || confidence > existing.confidence) {
        boundaryIndexByScene.set(sceneId, { index, confidence })
      }
    }

    const sceneStartCandidates = new Map<string, { index: number; confidence: number }>()

    canonical.canonical_anchors.forEach(anchor => {
      const sceneId = anchor.scene_id
      if (!sceneId) {
        return
      }
      if (anchor.reference_unit_id) {
        const idx = unitIndexById.get(anchor.reference_unit_id)
        if (idx !== undefined) {
          registerSceneStartCandidate(sceneStartCandidates, sceneId, idx, 1)
          return
        }
      }
      if (anchor.reference_text_signature) {
        const normalizedSignature = normalizeText(anchor.reference_text_signature)
        let idx = normalizedTextIndex.get(normalizedSignature)
        let candidateConfidence = anchor.confidence ?? 0.85
        if (idx === undefined) {
          const match = findBestMatchForPhrase(anchor.reference_text_signature, normalizedUnitTexts, unitTokensList, tokenFrequencies)
          if (match) {
            idx = match.index
            candidateConfidence = Math.max(candidateConfidence, match.confidence)
          }
        }
        if (idx !== undefined) {
          registerSceneStartCandidate(sceneStartCandidates, sceneId, idx, candidateConfidence)
          return
        }
      }
      if (anchor.reference_end_offset !== undefined) {
        const proportion = Math.min(Math.max(anchor.reference_end_offset, 0), 1)
        const idx = Math.min(totalUnits - 1, Math.max(0, Math.round(proportion * totalUnits) - 1))
        registerBoundary(sceneId, idx, 0.5)
      }
    })

    const sceneOrder = canonical.scene_order && canonical.scene_order.length > 0
      ? canonical.scene_order
      : canonical.scene_map.map(scene => scene.scene_id)

    // Phrase-based scene starts derived from canonical metadata.
    const boundaryPhraseMap = collectSceneBoundaryPhrases(canonical)
    sceneOrder.forEach(sceneId => {
      const phrases = boundaryPhraseMap.get(sceneId) ?? []
      phrases.forEach(entry => {
        const match = findBestMatchForPhrase(entry.phrase, normalizedUnitTexts, unitTokensList, tokenFrequencies)
        if (match) {
          const baseConfidence = Math.min(entry.confidence ?? 0.8, 0.95)
          const combinedConfidence = Math.max(baseConfidence, match.confidence)
          registerSceneStartCandidate(sceneStartCandidates, sceneId, match.index, combinedConfidence)
        }
      })
    })

    // Convert scene start candidates into boundaries for preceding scenes.
    sceneOrder.forEach((sceneId, idx) => {
      const candidate = sceneStartCandidates.get(sceneId)
      if (!candidate) {
        return
      }
      const prevSceneId = idx > 0 ? sceneOrder[idx - 1] : undefined
      if (!prevSceneId) {
        return
      }
      const boundaryIndex = candidate.index - 1
      if (boundaryIndex >= 0) {
        registerBoundary(prevSceneId, boundaryIndex, Math.min(candidate.confidence - 0.05, 0.9))
      }
    })

    const timestamp = nowIso()
    const assignments: WitnessSceneLayerRecord['assignments'] = []
    const sceneCount = sceneOrder.length || 1
    let previousEndIndex = -1

    sceneOrder.forEach((sceneId, index) => {
      const fallbackEnd = Math.round(((index + 1) / sceneCount) * totalUnits) - 1
      const boundary = boundaryIndexByScene.get(sceneId)
      let endIndex = boundary?.index ?? fallbackEnd
      if (index === sceneOrder.length - 1 || endIndex >= totalUnits) {
        endIndex = totalUnits - 1
      }

      if (endIndex <= previousEndIndex) {
        endIndex = Math.min(totalUnits - 1, previousEndIndex + Math.max(1, Math.floor(totalUnits / sceneCount)))
      }

      if (endIndex <= previousEndIndex) {
        return
      }

      const startIndex = previousEndIndex + 1
      if (startIndex >= totalUnits || endIndex < startIndex) {
        return
      }

      const startUnit = microUnits[startIndex]
      const endUnit = microUnits[endIndex]
      if (!startUnit || !endUnit) {
        return
      }

      const confidence = boundary?.confidence ?? 0.4

      assignments.push({
        scene_id: sceneId,
        start_unit_id: startUnit.unitId,
        end_unit_id: endUnit.unitId,
        start_chunk_id: startUnit.chunkId,
        end_chunk_id: endUnit.chunkId,
        confidence,
        status: 'auto'
      })

      previousEndIndex = endIndex
    })

    if (assignments.length === 0) {
      const firstUnit = microUnits[0]
      const lastUnit = microUnits[totalUnits - 1]
      const sceneId = sceneOrder[0] ?? 'scene-1'
      if (firstUnit && lastUnit) {
        assignments.push({
          scene_id: sceneId,
          start_unit_id: firstUnit.unitId,
          end_unit_id: lastUnit.unitId,
          start_chunk_id: firstUnit.chunkId,
          end_chunk_id: lastUnit.chunkId,
          confidence: 0.2,
          status: 'auto'
        })
      }
    }

    if (assignments.length === 0) {
      return undefined
    }

    const averageConfidence = assignments.length > 0
      ? assignments.reduce((sum, entry) => sum + (entry.confidence ?? 0), 0) / assignments.length
      : 0

    const derivedSceneLayer: SceneLayer = {
      version: canonical.version,
      scene_map: sceneOrder.map(sceneId => {
        const baseScene = canonical.scene_map.find(scene => scene.scene_id === sceneId)
        const assignment = assignments.find(entry => entry.scene_id === sceneId)
        return {
          scene_id: sceneId,
          title: baseScene?.title ?? sceneId,
          description: baseScene?.description,
          color: baseScene?.color,
          anchor_unit_id: assignment?.start_unit_id,
          anchor_chunk_id: assignment?.start_chunk_id,
          anchor_offset: undefined,
          created_at: baseScene?.created_at ?? timestamp
        }
      }),
      scene_order: sceneOrder,
      assignments: assignments.map(entry => ({
        scene_id: entry.scene_id,
        start_chunk_id: entry.start_chunk_id,
        end_chunk_id: entry.end_chunk_id,
        start_unit_id: entry.start_unit_id,
        end_unit_id: entry.end_unit_id,
        confidence: entry.confidence,
        origin: 'auto_map'
      })),
      metadata: {
        generated_at: timestamp,
        strategy: 'auto_map_from_canonical',
        canonical_version: canonical.version,
        auto_applied: true,
        auto_applied_version: canonical.version,
        average_assignment_confidence: Number(averageConfidence.toFixed(4))
      }
    }

    const autoMappedRecord = get().createWitnessRecordFromSceneLayer(
      witnessId,
      chapterId,
      bookId,
      derivedSceneLayer,
      canonical.version,
      'auto'
    )

    await dbStorage.put('witness_scenes', autoMappedRecord)
    set(prev => {
      const next = new Map(prev.witnessLayers)
      next.set(witnessId, autoMappedRecord)
      const nextPending = new Map(prev.pendingRemapByChapter)
      const pendingSet = nextPending.get(chapterId)
      if (pendingSet?.has(witnessId)) {
        const updated = new Set(pendingSet)
        updated.delete(witnessId)
        if (updated.size === 0) {
          nextPending.delete(chapterId)
        } else {
          nextPending.set(chapterId, updated)
        }
      }
      return { witnessLayers: next, pendingRemapByChapter: nextPending }
    })

    removePendingRemap(witnessId, chapterId)
    return autoMappedRecord
  },

  confirmAutoAssignments: async (witnessId: string) => {
    const state = get()
    let baseRecord = state.witnessLayers.get(witnessId)

    if (!baseRecord) {
      const persistedRecord = await dbStorage.get('witness_scenes', witnessId)
      if (!persistedRecord) {
        return
      }
      set(prev => {
        const next = new Map(prev.witnessLayers)
        next.set(witnessId, persistedRecord)
        return { witnessLayers: next }
      })
      baseRecord = persistedRecord
    }

    if (!baseRecord) {
      return
    }

    const hasAutoAssignments = baseRecord.assignments.some(assignment => assignment.status === 'auto')
    if (!hasAutoAssignments) {
      return
    }

    const timestamp = nowIso()
    const updatedRecord: WitnessSceneLayerRecord = {
      ...baseRecord,
      assignments: baseRecord.assignments.map(assignment =>
        assignment.status === 'auto'
          ? {
              ...assignment,
              status: 'confirmed',
              last_reviewed_at: timestamp
            }
          : assignment
      ),
      metadata: {
        ...baseRecord.metadata,
        strategy: 'auto_map_from_canonical',
        auto_applied: (baseRecord.metadata as any)?.auto_applied ?? true,
        last_confirmed_auto_review_at: timestamp
      }
    }

    await get().saveWitnessSceneLayer(updatedRecord, {
      expectedUpdatedAt: baseRecord.updated_at
    })
  },

  registerMicroUnits: (witnessId, microUnits) => {
    cacheMicroUnits(witnessId, microUnits)
  },

  unregisterMicroUnits: (witnessId) => {
    cacheMicroUnits(witnessId, [])
  },

  promoteWitnessScenesToCanonical: async ({ witnessId, chapterId, bookId, microUnits, promotedBy }) => {
    const state = get()
    let witnessRecord = state.witnessLayers.get(witnessId)
    if (!witnessRecord) {
      witnessRecord = await dbStorage.get('witness_scenes', witnessId)
      if (!witnessRecord) {
        throw new Error('No witness scenes available to promote')
      }
    }

    if (!Array.isArray(witnessRecord.assignments) || witnessRecord.assignments.length === 0) {
      throw new Error('Cannot promote: witness has no scene assignments')
    }

    const canonicalVersionTracker = state.canonicalVersionCounter.get(chapterId) ?? 0
    const existingCanonical = state.canonicalLayers.get(chapterId)
    const nextVersion = canonicalVersionTracker + 1
    const now = nowIso()

    const overridesByScene = new Map(
      (witnessRecord.overrides ?? []).map(override => [override.scene_id, override])
    )

    const unitById = new Map(
      microUnits.map(unit => [unit.unitId, unit])
    )

    const sceneOrder = witnessRecord.assignments.map(assignment => assignment.scene_id)

    const anchors = witnessRecord.assignments.map(assignment => {
      const startUnit = unitById.get(assignment.start_unit_id ?? '')
      const signatureSource = startUnit?.text ?? ''
      const signatureTokens = signatureSource.split(/\s+/).slice(0, 18).join(' ')
      return {
        scene_id: assignment.scene_id,
        reference_witness_id: witnessId,
        reference_unit_id: assignment.start_unit_id,
        reference_chunk_id: assignment.start_chunk_id,
        reference_text_signature: signatureTokens,
        confidence: 0.95
      }
    })

    const sceneMap = sceneOrder.map(sceneId => {
      const assignment = witnessRecord!.assignments.find(entry => entry.scene_id === sceneId)
      const override = overridesByScene.get(sceneId)
      const startUnit = assignment?.start_unit_id ? unitById.get(assignment.start_unit_id) : undefined
      const startText = startUnit?.text ?? ''
      const primarySignature = startText.split(/\s+/).slice(0, 18).join(' ')
      const derivedPhrases = primarySignature ? derivePhrasesFromSignature(primarySignature) : []

      const boundaryPhrases: SceneBoundaryPhrase[] = []
      if (primarySignature) {
        boundaryPhrases.push({
          phrase: primarySignature,
          confidence: 0.95,
          source: 'canonical_start_unit'
        })
      }
      derivedPhrases.forEach(phrase => {
        boundaryPhrases.push({
          phrase,
          confidence: 0.8,
          source: 'derived_ngram'
        })
      })

      return {
        scene_id: sceneId,
        title: override?.title ?? sceneId,
        description: override?.metadata?.description,
        color: override?.color,
        anchor_unit_id: assignment?.start_unit_id,
        anchor_chunk_id: assignment?.start_chunk_id,
        created_at: existingCanonical?.scene_map?.find(scene => scene.scene_id === sceneId)?.created_at ?? now,
        ui_metadata: existingCanonical?.scene_map?.find(scene => scene.scene_id === sceneId)?.ui_metadata,
        boundary_phrases: boundaryPhrases
      }
    })

    const canonicalLayer: ChapterSceneLayer = {
      chapter_id: chapterId,
      book_id: bookId,
      version: nextVersion,
      scene_order: sceneOrder,
      scene_map: sceneMap,
      canonical_anchors: anchors,
      metadata: {
        ...(existingCanonical?.metadata || {}),
        created_at: existingCanonical?.metadata?.created_at ?? now,
        updated_at: now,
        source_witness_id: witnessId,
        promoted_by: promotedBy ?? 'manual'
      },
      created_at: existingCanonical?.created_at ?? now,
      updated_at: now
    }

    await get().saveChapterSceneLayer(canonicalLayer)
    return canonicalLayer
  },

  applyCanonicalScenesToWitness: async ({ witnessId, chapterId, bookId, microUnits }) => {
    return get().ensureAutoMappedWitnessLayer(
      { witnessId, chapterId, bookId, microUnits },
      { force: true }
    )
  }
}
});
